import { useMemo, useState } from "react";
import { Link } from "react-router";
import { AlertCircle, Calendar, CheckCircle2, Play, Receipt } from "lucide-react";
import { useRunMonthlyInvoiceGenerationMutation } from "../../redux/apis/billingApi";

const BILLING_CYCLE_DAYS = [
  { day: 15, label: "15th Billing Cycle", code: 15 },
  { day: 20, label: "20th Billing Cycle", code: 20 },
  { day: 25, label: "25th Billing Cycle", code: 25 },
  { day: "eom", label: "End of Month (EOM)", code: 30 },
];

function todayInputValue() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseLocalDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? "").trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return new Date(year, month - 1, day);
}

function billingCycleForDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  const day = date.getDate();
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  if (day === 15) {
    return BILLING_CYCLE_DAYS[0];
  }
  if (day === 20) {
    return BILLING_CYCLE_DAYS[1];
  }
  if (day === 25) {
    return BILLING_CYCLE_DAYS[2];
  }
  if (day === lastDay) {
    return BILLING_CYCLE_DAYS[3];
  }
  return null;
}

function serviceMonthForRunDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const next = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  const y = next.getFullYear();
  const m = String(next.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function formatApiError(err) {
  if (err?.status === 403) {
    return "You do not have permission to run this job (MANUALLY_RUN_INVOICE_CREATION_JOB required).";
  }
  const detail = err?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof err?.data?.message === "string" && err.data.message.trim()) {
    return err.data.message.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `Unable to run job (HTTP ${err.status}).`;
  }
  return "Unable to run job.";
}

function AutomatedInvoiceCreateJob() {
  const [runDate, setRunDate] = useState(todayInputValue);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [runError, setRunError] = useState("");

  const [runJob, { isLoading: isRunning }] = useRunMonthlyInvoiceGenerationMutation();

  const selectedDate = useMemo(() => parseLocalDate(runDate), [runDate]);
  const cycleInfo = useMemo(() => billingCycleForDate(selectedDate), [selectedDate]);
  const targetServiceMonth = useMemo(
    () => serviceMonthForRunDate(selectedDate),
    [selectedDate]
  );

  const handleRun = async () => {
    setRunError("");
    setConfirmOpen(false);
    try {
      const result = await runJob({ runDate: runDate.trim() || undefined }).unwrap();
      setLastResult(result);
    } catch (err) {
      setLastResult(null);
      setRunError(formatApiError(err));
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-semibold text-gray-900 dark:text-gray-100">
          Automated Invoice Create Job
        </h1>
        <p className="max-w-3xl text-gray-600 dark:text-gray-400">
          Manually run the monthly draft invoice job. On a billing-cycle day this creates
          next-month <span className="font-medium">Draft</span> invoices for eligible active
          invoice profiles, with line quantities initialized to zero.
        </p>
      </div>

      <div className="mb-6 grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded bg-blue-100 p-2 dark:bg-blue-900">
              <Receipt className="h-5 w-5 text-blue-600 dark:text-blue-300" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Run job</h2>
          </div>

          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Run date
          </label>
          <div className="relative mb-4 max-w-xs">
            <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={runDate}
              onChange={(e) => setRunDate(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>

          {cycleInfo ? (
            <div className="mb-4 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-100">
              <p>
                <span className="font-medium">{runDate}</span> is a billing-cycle day (
                {cycleInfo.label}). Target service month:{" "}
                <span className="font-medium">{targetServiceMonth}</span>.
              </p>
            </div>
          ) : (
            <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
              <p>
                <span className="font-medium">{runDate || "Selected date"}</span> is not a
                billing-cycle day. The job will complete with no invoices created. Use the 15th,
                20th, 25th, or last day of the month.
              </p>
            </div>
          )}

          <button
            type="button"
            className="app-btn-gloss inline-flex items-center gap-2"
            disabled={isRunning}
            onClick={() => setConfirmOpen(true)}
          >
            <Play className="h-4 w-4" />
            {isRunning ? "Running…" : "Run job"}
          </button>
        </div>

        <div className="rounded border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
            Billing cycle days
          </h2>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            {BILLING_CYCLE_DAYS.map((cycle) => (
              <li key={cycle.code}>
                <span className="font-medium">{cycle.label}</span>
                <span className="text-gray-500 dark:text-gray-400">
                  {" "}
                  — contract billing_cycle = {cycle.code}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            One draft per invoice profile per service month. System-created rows use{" "}
            <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">created_by = -1</code>.
          </p>
        </div>
      </div>

      {runError ? (
        <div className="mb-6 max-w-5xl rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{runError}</span>
          </div>
        </div>
      ) : null}

      {lastResult ? (
        <div className="max-w-5xl rounded border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Last run</h2>
          </div>
          <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
            {lastResult.message}
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <div>
              <p className="text-gray-500 dark:text-gray-400">Created</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {lastResult.profilesCreated ?? 0}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">Skipped</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {lastResult.profilesSkipped ?? 0}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">Failed</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {lastResult.profilesFailed ?? 0}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">Service month</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {lastResult.serviceMonth || "—"}
              </p>
            </div>
          </div>
          <div className="mt-6">
            <Link
              to="/invoices"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              View invoices →
            </Link>
          </div>
        </div>
      ) : null}

      {confirmOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-run-job-title"
        >
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <h3
              id="confirm-run-job-title"
              className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Run automated invoice job?
            </h3>
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              This will create draft invoices for eligible profiles using run date{" "}
              <span className="font-medium">{runDate}</span>
              {targetServiceMonth ? (
                <>
                  {" "}
                  and service month <span className="font-medium">{targetServiceMonth}</span>
                </>
              ) : null}
              . Existing invoices for the same profile and month are skipped.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                onClick={() => setConfirmOpen(false)}
                disabled={isRunning}
              >
                Cancel
              </button>
              <button
                type="button"
                className="app-btn-gloss text-sm"
                onClick={handleRun}
                disabled={isRunning}
              >
                {isRunning ? "Running…" : "Confirm run"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { AutomatedInvoiceCreateJob };
